import { groupBy } from "lodash";

import type { Acknowledgement, AcknowledgementSubject } from "@storyboard/web-client";
import { store } from "@/store/store";
import type {
  AnchorAcknowledgementRow,
  FeedAcknowledgementRow,
  FeedItemAcknowledgementRow,
  TaskAcknowledgementRow,
} from "@/store/types";

/**
 * Every subject the client syncs, one log each.
 *
 * Written as a record the compiler checks against the generated union, so a subject the API adds fails to compile
 * here until it is placed.
 */
export const ACKNOWLEDGEMENT_SUBJECTS: readonly AcknowledgementSubject[] = Object.keys({
  feed: true,
  feed_item: true,
  stop_event: true,
  form_submission: true,
  hos_clock_violation: true,
  route_assignment_event: true,
  task: true,
} satisfies Record<AcknowledgementSubject, true>) as AcknowledgementSubject[];

/** The store collection that holds each subject's log. */
export const ACKNOWLEDGEMENT_COLLECTION = {
  feed: "feedAcknowledgement",
  feed_item: "feedItemAcknowledgement",
  stop_event: "anchorAcknowledgement",
  form_submission: "anchorAcknowledgement",
  hos_clock_violation: "anchorAcknowledgement",
  route_assignment_event: "anchorAcknowledgement",
  task: "taskAcknowledgement",
} as const satisfies Record<AcknowledgementSubject, string>;

/** The subjects whose target is a TMS event's anchor, every one written and served by the anchor's id. */
export type AnchorAcknowledgementSubject = {
  [S in AcknowledgementSubject]: (typeof ACKNOWLEDGEMENT_COLLECTION)[S] extends "anchorAcknowledgement" ? S : never;
}[AcknowledgementSubject];

/** Whether a subject's log targets anchors, and so shares the one anchor collection. */
export function isAnchorAcknowledgementSubject(subject: string): subject is AnchorAcknowledgementSubject {
  return (ACKNOWLEDGEMENT_COLLECTION as Record<string, string>)[subject] === "anchorAcknowledgement";
}

/**
 * Upserts acknowledgement rows into their subjects' collections.
 *
 * Rows are keyed on their id and a repeat replaces the row it matches: a sync re-serving rows near its cursor writes
 * the same values again, and the server's echo of an optimistic write replaces the client's estimated `createdAt` with
 * the stored one.
 */
export function upsertAcknowledgements(rows: readonly Acknowledgement[]): void {
  const byCollection = groupBy(rows, (row) => ACKNOWLEDGEMENT_COLLECTION[row.subject]);
  const { feedAcknowledgement, feedItemAcknowledgement, anchorAcknowledgement, taskAcknowledgement } = byCollection;
  if (feedAcknowledgement) store.upsert("feedAcknowledgement", feedAcknowledgement as FeedAcknowledgementRow[]);
  if (feedItemAcknowledgement) {
    store.upsert("feedItemAcknowledgement", feedItemAcknowledgement as FeedItemAcknowledgementRow[]);
  }
  if (anchorAcknowledgement) store.upsert("anchorAcknowledgement", anchorAcknowledgement as AnchorAcknowledgementRow[]);
  if (taskAcknowledgement) store.upsert("taskAcknowledgement", taskAcknowledgement as TaskAcknowledgementRow[]);
}

/** Removes rows the server dropped, so an optimistic acknowledgement of a target that is gone does not linger. */
export function removeAcknowledgements(subject: AcknowledgementSubject, ids: readonly string[]): void {
  if (ids.length === 0) return;
  store.delete(ACKNOWLEDGEMENT_COLLECTION[subject], ids);
}
